import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  Users,
  DollarSign,
  Eye,
  Grid3X3,
  CalendarDays,
  Plus,
  CheckCircle,
  XCircle,
  User,
  Briefcase,
  Filter,
  Search,
  MoreHorizontal,
  Edit3,
  Trash2,
  AlertCircle,
  BarChart3
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { consultantsService, ConsultantService } from '../../services/consultants.service';
import { authService } from '../../services/auth.service';

interface CalendarEvent {
  id: string;
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  service: ConsultantService;
  creneau: any;
  status: 'available' | 'booked' | 'pending';
  client?: {
    name: string;
    email: string;
  };
}

const CalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [services, setServices] = useState<ConsultantService[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredEvent, setHoveredEvent] = useState<CalendarEvent | null>(null);
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>('month');
  const [selectedService, setSelectedService] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showServiceFilter, setShowServiceFilter] = useState(false);

  useEffect(() => {
    fetchServicesWithCreneaux();
  }, []);

  useEffect(() => {
    processEvents();
  }, [services, selectedService, searchTerm]);

  const fetchServicesWithCreneaux = async () => {
    try {
      setLoading(true);
      const data = await consultantsService.getMyServicesWithCreneaux();
      setServices(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const processEvents = () => {
    const allEvents: CalendarEvent[] = [];
    
    services.forEach(service => {
      if (service.creneaux && service.creneaux.length > 0) {
        service.creneaux.forEach(creneau => {
          const event: CalendarEvent = {
            id: `event-${creneau.id}`,
            title: service.title,
            date: creneau.creneau_date,
            startTime: creneau.start_time,
            endTime: creneau.end_time,
            service: service,
            creneau: creneau,
            status: creneau.is_booked ? 'booked' : creneau.is_available ? 'available' : 'pending',
            client: creneau.trainee ? {
              name: `${creneau.trainee.firstname} ${creneau.trainee.lastname}`,
              email: creneau.trainee.email
            } : undefined
          };
          allEvents.push(event);
        });
      }
    });

    // Filter events based on selected service and search term
    let filteredEvents = allEvents;
    
    if (selectedService !== 'all') {
      filteredEvents = filteredEvents.filter(event => event.service.id.toString() === selectedService);
    }
    
    if (searchTerm) {
      filteredEvents = filteredEvents.filter(event => 
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.service.domaine?.name_french.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (event.client && event.client.name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setEvents(filteredEvents);
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek };
  };

  const getEventsForDate = (date: Date) => {
    // Format date as YYYY-MM-DD without timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    return events.filter(event => 
      event.date === dateString
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'booked': return 'bg-green-500';
      case 'available': return 'bg-blue-500';
      case 'pending': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'booked': return CheckCircle;
      case 'available': return Clock;
      case 'pending': return AlertCircle;
      default: return Clock;
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'long' 
    });
  };

  const formatTime = (time: string) => {
    return time.substring(0, 5); // Remove seconds
  };

  const goToPreviousMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getWeekDays = () => {
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day;
    startOfWeek.setDate(diff);
    
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      weekDays.push(date);
    }
    return weekDays;
  };

  const getMonthDays = () => {
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      days.push(date);
    }
    return days;
  };


  const days = viewMode === 'week' ? getWeekDays() : getMonthDays();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-600/90 to-blue-600/80 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                <CalendarIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>
              <h1 className="text-2xl sm:text-4xl font-bold">
                {t('consultant.calendar.title')}
              </h1>
            </div>
            <p className="text-white/90 text-base sm:text-lg">
              {t('consultant.calendar.subtitle')}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 mt-6 lg:mt-0">
             {/* View Mode Toggle */}
             <div className="flex bg-white/20 backdrop-blur-sm rounded-xl p-1 w-full sm:w-auto">
               <button
                 onClick={() => setViewMode('month')}
                 className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-1 sm:gap-2 ${
                   viewMode === 'month'
                     ? 'bg-white text-purple-600 shadow-lg'
                     : 'text-white hover:bg-white/10'
                 }`}
               >
                 <Grid3X3 className="h-3 w-3 sm:h-4 sm:w-4" />
                 <span className="hidden sm:inline">{t('consultant.calendar.month')}</span>
                 <span className="sm:hidden">M</span>
               </button>
               <button
                 onClick={() => setViewMode('week')}
                 className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-1 sm:gap-2 ${
                   viewMode === 'week'
                     ? 'bg-white text-purple-600 shadow-lg'
                     : 'text-white hover:bg-white/10'
                 }`}
               >
                 <CalendarDays className="h-3 w-3 sm:h-4 sm:w-4" />
                 <span className="hidden sm:inline">{t('consultant.calendar.week')}</span>
                 <span className="sm:hidden">S</span>
               </button>
             </div>
             <button
               onClick={goToToday}
               className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-semibold text-purple-600 bg-white rounded-xl hover:bg-purple-600/10 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
             >
               {t('consultant.calendar.today')}
             </button>
           </div>
        </div>
      </div>

      {/* Calendar Navigation */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={goToPreviousMonth}
            className="p-3 text-gray-400 hover:text-purple-600 hover:bg-purple-600/10 rounded-xl transition-all duration-200 group"
          >
            <ChevronLeft className="h-6 w-6 group-hover:scale-110 transition-transform" />
          </button>
          
          <h2 className="text-2xl font-bold text-gray-900 capitalize bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
            {formatDate(currentDate)}
          </h2>
          
          <button
            onClick={goToNextMonth}
            className="p-3 text-gray-400 hover:text-purple-600 hover:bg-purple-600/10 rounded-xl transition-all duration-200 group"
          >
            <ChevronRight className="h-6 w-6 group-hover:scale-110 transition-transform" />
          </button>
        </div>

                 {/* Calendar Grid */}
         <div className={`grid grid-cols-7 gap-2 ${viewMode === 'week' ? 'min-h-[600px]' : ''}`}>
           {/* Day Headers */}
           {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map(day => (
             <div key={day} className="p-4 text-center text-sm font-bold text-purple-600 bg-purple-600/10 rounded-xl">
               {day}
             </div>
           ))}

           {/* Calendar Days */}
           {days.map((date, index) => {
             if (!date) {
               return <div key={index} className={`p-4 bg-gray-50 rounded-xl ${viewMode === 'week' ? 'min-h-[600px]' : 'min-h-[140px]'}`}></div>;
             }

             const dayEvents = getEventsForDate(date);
             const isToday = date.toDateString() === new Date().toDateString();
             const isCurrentMonth = viewMode === 'month' ? date.getMonth() === currentDate.getMonth() : true;

             return (
               <div
                 key={index}
                 className={`p-4 border-2 rounded-xl relative transition-all duration-200 hover:shadow-lg ${
                   viewMode === 'week' ? 'min-h-[600px]' : 'min-h-[140px]'
                 } ${
                   isToday 
                     ? 'bg-gradient-to-br from-emerald-500 to-purple-600 border-purple-600/40 shadow-lg' 
                     : 'bg-white border-gray-100 hover:border-purple-600/20'
                 } ${viewMode === 'month' && !isCurrentMonth ? 'opacity-40' : ''}`}
               >
                 {/* Day Number */}
                 <div className={`text-lg font-bold mb-3 ${
                   isToday ? 'text-white' : 'text-gray-900'
                 }`}>
                   {date.getDate()}
                 </div>

                 {/* Calendar Events */}
                 <div className="space-y-2">
                   {dayEvents.slice(0, viewMode === 'week' ? 8 : 2).map((event) => {
                     const StatusIcon = getStatusIcon(event.status);
                     return (
                       <div
                         key={event.id}
                         className="relative group"
                         onMouseEnter={() => setHoveredEvent(event)}
                         onMouseLeave={() => setHoveredEvent(null)}
                       >
                         <button
                           onClick={() => navigate(`/consultant/services/${event.service.id}/creneaux`)}
                           className={`w-full text-left p-3 rounded-lg text-xs font-semibold transition-all duration-200 cursor-pointer transform hover:scale-105 ${getStatusColor(event.status)} text-white`}
                         >
                           <div className="flex items-center gap-2">
                             <StatusIcon className="h-3 w-3" />
                             <div className="truncate font-medium">{event.title}</div>
                           </div>
                           <div className="text-xs mt-1 opacity-90">
                             {formatTime(event.startTime)} - {formatTime(event.endTime)}
                           </div>
                           {event.client && (
                             <div className="text-xs mt-1 font-medium opacity-90">
                               Client: {event.client.name}
                             </div>
                           )}
                         </button>

                       {/* Hover Tooltip */}
                       {hoveredEvent?.id === event.id && (
                         <motion.div
                           initial={{ opacity: 0, y: 10, scale: 0.95 }}
                           animate={{ opacity: 1, y: 0, scale: 1 }}
                           exit={{ opacity: 0, y: 10, scale: 0.95 }}
                           className="absolute z-50 left-full ml-3 top-0 w-80 bg-white border-2 border-purple-600/20 rounded-2xl shadow-2xl p-6"
                         >
                           <div className="space-y-4">
                             <div>
                               <h4 className="font-bold text-gray-900 text-sm">
                                 {event.title}
                               </h4>
                               {event.service.domaine && (
                                 <p className="text-xs text-purple-600 font-medium">
                                   {event.service.domaine.name_french}
                                 </p>
                               )}
                               <p className="text-xs text-gray-500 font-medium">
                                 Créneau du {new Date(event.date).toLocaleDateString('fr-FR')}
                               </p>
                             </div>

                             <div className="space-y-3">
                               <div className="flex items-center gap-3 text-xs text-gray-600">
                                 <div className="p-1.5 bg-purple-600/20 rounded-lg">
                                   <Clock className="h-3 w-3 text-purple-600" />
                                 </div>
                                 <span className="font-medium">{formatTime(event.startTime)} - {formatTime(event.endTime)}</span>
                               </div>
                               
                               <div className="flex items-center gap-3 text-xs text-gray-600">
                                 <div className="p-1.5 bg-purple-600/20 rounded-lg">
                                   <MapPin className="h-3 w-3 text-purple-600" />
                                 </div>
                                 <span className="font-medium">
                                   {event.service.city && event.service.country 
                                     ? `${event.service.city}, ${typeof event.service.country === 'string' ? event.service.country : event.service.country?.name}`
                                     : t('common.locationNotSpecified')
                                   }
                                 </span>
                               </div>

                               <div className="flex items-center gap-3 text-xs text-gray-600">
                                 <div className="p-1.5 bg-purple-600/20 rounded-lg">
                                   <Users className="h-3 w-3 text-purple-600" />
                                 </div>
                                 <span className="font-medium">Format: {event.service.format}</span>
                               </div>

                               {event.client && (
                                 <div className="flex items-center gap-3 text-xs text-gray-600">
                                   <div className="p-1.5 bg-purple-600/20 rounded-lg">
                                     <User className="h-3 w-3 text-purple-600" />
                                   </div>
                                 <span className="font-medium">{t('consultant.calendar.client')}: {event.client.name}</span>
                                 </div>
                               )}

                               {event.client && (
                                 <div className="flex items-center gap-3 text-xs text-gray-600">
                                   <div className="p-1.5 bg-purple-600/20 rounded-lg">
                                     <User className="h-3 w-3 text-purple-600" />
                                   </div>
                                   <span className="font-medium">{event.client.email}</span>
                                 </div>
                               )}
                             </div>

                             <button
                               onClick={() => navigate(`/consultant/services/${event.service.id}/creneaux`)}
                               className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-bold rounded-xl hover:from-purple-600/90 hover:to-blue-600/80 transition-all duration-200 transform hover:scale-105 shadow-lg"
                             >
                               <Eye className="h-4 w-4" />
                               {t('consultant.calendar.manageSlots')}
                             </button>
                           </div>
                         </motion.div>
                       )}
                     </div>
                   );
                 })}

                   {dayEvents.length > (viewMode === 'week' ? 8 : 2) && (
                     <div className={`text-xs text-center font-medium ${
                       isToday ? 'text-white/90' : 'text-purple-600'
                     }`}>
                       +{dayEvents.length - (viewMode === 'week' ? 8 : 2)} {t('consultant.calendar.otherSlots')}
                     </div>
                   )}
                 </div>
               </div>
             );
           })}
         </div>
      </div>

      {/* Summary Statistics */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
          <div className="p-2 bg-purple-600/20 rounded-lg">
            <BarChart3 className="h-5 w-5 text-purple-600" />
          </div>
          {t('consultant.calendar.summary')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600 mb-2">{events.length}</div>
            <div className="text-sm text-gray-600">{t('consultant.calendar.totalSlots')}</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600 mb-2">
              {events.filter(e => e.status === 'available').length}
            </div>
            <div className="text-sm text-gray-600">{t('consultant.calendar.available')}</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-red-600 mb-2">
              {events.filter(e => e.status === 'booked').length}
            </div>
            <div className="text-sm text-gray-600">{t('consultant.calendar.booked')}</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-yellow-600 mb-2">
              {events.filter(e => e.status === 'pending').length}
            </div>
            <div className="text-sm text-gray-600">{t('consultant.calendar.pending')}</div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
          <div className="p-2 bg-purple-600/20 rounded-lg">
            <CalendarIcon className="h-5 w-5 text-purple-600" />
          </div>
          {t('consultant.calendar.legend')}
        </h3>
        <div className="flex flex-wrap gap-6">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-green-100 border border-green-200 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-3 w-3 text-green-600" />
            </div>
            <span className="text-sm font-medium text-gray-700">{t('consultant.calendar.slotAvailable')}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-red-100 border border-red-200 rounded-lg flex items-center justify-center">
              <XCircle className="h-3 w-3 text-red-600" />
            </div>
            <span className="text-sm font-medium text-gray-700">{t('consultant.calendar.slotBooked')}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center">
              <Clock className="h-3 w-3 text-gray-600" />
            </div>
            <span className="text-sm font-medium text-gray-700">{t('consultant.calendar.slotUnavailable')}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-gradient-to-br from-emerald-500 to-purple-600 rounded-lg border-2 border-purple-600/40"></div>
            <span className="text-sm font-medium text-gray-700">{t('consultant.calendar.today')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarPage;